import { useEffect, useRef, type RefObject } from "react";

// ROLAR ATÉ O RESULTADO DA ESCOLHA — num lugar só.
//
// ===========================================================================
// O defeito, e onde ele estava
// ===========================================================================
// Victor achou o mesmo problema em três telas, em 28/08/2026: **a escolha
// acontece longe do resultado dela, e nada leva o olho até lá.**
//
//   · Mapa de Concursos — clicar num estado no mapa filtra a lista, que está
//     abaixo da cabine navy inteira;
//   · Calendário Tributário — clicar num dia da grade filtra a rubrica, que está
//     abaixo da grade;
//   · Admin › Alunos — escolher um aluno da busca abre o caso dele, abaixo da
//     lista de resultados.
//
// Nos três a tela responde — o conteúdo TROCA —, mas fora do campo de visão. O
// modo de falha é o pior tipo: a pessoa clica, não vê nada mudar, e conclui que
// o clique não funcionou.
//
// ===========================================================================
// Por que o `scroll-mt` não era acoplamento (e o comentário que dizia isso saiu)
// ===========================================================================
// `CalendarioUi.tsx` trazia esta decisão escrita: *"Clicar num dia FILTRA a
// lista; não rola até ela. Rolar exigiria cravar a altura do `Topo` num
// `scroll-mt` — acoplamento silencioso que quebra quando o topo muda"*.
//
// A objeção está certa e a conclusão não: **não é preciso cravar nada.** O hub
// tem `--cf-topo` no `index.css` exatamente para isto, e o comentário dela diz
// o porquê — *"Se o Topo mudar de altura, muda AQUI e todo mundo acompanha"*.
// Então o deslocamento sai da variável, e o acoplamento que a decisão temia não
// existe.
//
// ===========================================================================
// Duas coisas que rolar sozinho exige
// ===========================================================================
// **1. Respeitar quem pediu menos movimento.** `prefers-reduced-motion: reduce`
// é preferência de acessibilidade de gente que sente enjoo com rolagem animada.
// Aí o salto é instantâneo — ele continua levando ao lugar, sem o movimento.
//
// **2. Não roubar o FOCO.** Rolar move a vista; mover o foco moveria o teclado.
// Quem escolheu um estado com o teclado ainda está no botão do estado e pode
// querer escolher outro — puxar o foco para a lista quebraria essa sequência. A
// peça rola e para por aí; o `aria-live` de quem chama é que anuncia a mudança
// para o leitor de tela, se for o caso.

/**
 * Rola a página até `el`, com o Topo fixo descontado.
 *
 * Não faz nada quando o elemento já está inteiro na tela: rolar o que já se vê
 * é movimento gratuito, e num mês com um vencimento só a lista já estava
 * visível — que era o outro argumento do comentário antigo, e esse continua
 * valendo.
 */
export function rolarAte(el: HTMLElement | null): void {
  if (!el || typeof window === "undefined") return;

  const topo = alturaDoTopo();
  const caixa = el.getBoundingClientRect();
  const jaVisivel = caixa.top >= topo && caixa.bottom <= window.innerHeight;
  if (jaVisivel) return;

  const reduzido = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
  window.scrollTo({
    top: window.scrollY + caixa.top - topo - 12,
    behavior: reduzido ? "auto" : "smooth",
  });
}

/**
 * A altura do Topo fixo, lida de `--cf-topo`.
 *
 * A variável está em `rem`; o navegador devolve o valor cru (`"4.25rem"`), então
 * a conversão usa o tamanho de fonte da raiz — que é o que `rem` significa e o
 * que muda quando alguém aumenta a fonte do navegador. Cravar 68px funcionaria
 * hoje e mentiria para quem lê com fonte grande.
 *
 * O painel da OPERAÇÃO tem cabeçalho fixo próprio, e ele não é o Topo do aluno:
 * tem o logo, os dois grupos de abas e os rótulos deles — medido em 29/09,
 * 143px contra os 68 da variável. Descontando a variável ali, toda tela do
 * painel que rolava até um resultado o deixava embaixo do cabeçalho: o título da
 * lista de comentários parava em 80px, 63px escondido. Quem achou foi a prova da
 * paginação das avaliações, medindo contra o cabeçalho de verdade. Então o
 * cabeçalho que não é o Topo se DECLARA (`data-topo-fixo`), e vale a altura que
 * ele tem na tela, que muda com as abas que cada pessoa vê.
 */
function alturaDoTopo(): number {
  const fixo = document.querySelector<HTMLElement>("[data-topo-fixo]");
  if (fixo) return Math.max(0, fixo.getBoundingClientRect().bottom);
  const bruto = getComputedStyle(document.documentElement).getPropertyValue("--cf-topo").trim();
  const base = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
  if (bruto.endsWith("rem")) return (parseFloat(bruto) || 0) * base;
  if (bruto.endsWith("px")) return parseFloat(bruto) || 0;
  // Sem a variável (tela fora da moldura do hub), não há topo a descontar.
  return 0;
}

/**
 * Rola até o alvo quando `gatilho` muda — pulando o primeiro render.
 *
 * O primeiro render é a ABERTURA da tela, e rolar ali jogaria o aluno para o
 * meio da página antes de ele ler o começo. É o mesmo cuidado do
 * `FerramentaDeConteudo`, que rola para o TOPO ao trocar de página em vez de
 * deixar a rolagem onde estava.
 *
 * `gatilho` nulo (nada selecionado) também não rola: desmarcar um filtro não é
 * escolher um resultado.
 */
export function useRolarAoEscolher(
  alvo: RefObject<HTMLElement | null>,
  gatilho: string | null,
): void {
  const primeiro = useRef(true);
  useEffect(() => {
    if (primeiro.current) {
      primeiro.current = false;
      return;
    }
    if (gatilho === null) return;
    // Um quadro de espera: o React já pintou o conteúdo novo, mas o layout pode
    // ainda estar assentando (a lista encolheu, a rubrica apareceu). Medir a
    // caixa antes disso rola para a posição VELHA — e o sintoma é uma rolagem
    // que para no lugar errado por alguns pixels.
    const t = window.setTimeout(() => rolarAte(alvo.current), 60);
    return () => window.clearTimeout(t);
  }, [gatilho, alvo]);
}
